import api from './api.js';

// Filter forms hold '' for "no filter". The API rejects empty strings
// (search, UUID filters), so they must be omitted, not sent.
export function cleanParams(params = {}) {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== '' && value !== null && value !== undefined)
  );
}

const withCleanParams = (config) =>
  config?.params ? { ...config, params: cleanParams(config.params) } : config;

const adminApi = {
  // Generic methods for direct API access
  get: (url, config) => api.get(url, withCleanParams(config)),
  post: (url, data, config) => api.post(url, data, config),
  put: (url, data, config) => api.put(url, data, config),
  patch: (url, data, config) => api.patch(url, data, config),
  delete: (url, config) => api.delete(url, config),

  // Get all site goals
  getGoals: () => api.get('/admin/goals'),

  // Get a specific site goal
  getGoal: (id) => api.get(`/admin/goals/${id}`),

  // Create a new site goal
  createGoal: (goalData) => api.post('/admin/goals', goalData),

  // Update a site goal
  updateGoal: (id, goalData) => api.put(`/admin/goals/${id}`, goalData),

  // Delete a site goal
  deleteGoal: (id) => api.delete(`/admin/goals/${id}`),

  // Update all goal current values
  updateGoalValues: () => api.post('/admin/goals/update-current-values'),

  // Get admin dashboard
  getDashboard: () => api.get('/admin/dashboard'),

  // User Management
  getUsers: (params = {}) => api.get('/admin/users', { params: cleanParams(params) }),

  getUser: (userId) => api.get(`/admin/users/${userId}`),

  createUser: (userData) => api.post('/admin/users', userData),

  updateUser: (userId, changes) => api.patch(`/admin/users/${userId}`, changes),

  // What a delete would destroy, and anything that must be reassigned first
  getUserImpact: (userId) => api.get(`/admin/users/${userId}/impact`),

  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),

  resetUserPassword: (userId, newPassword) => api.post(`/admin/users/${userId}/reset-password`, { newPassword }),

  sendUserPasswordResetLink: (userId) => api.post(`/admin/users/${userId}/reset-password`, { sendResetLink: true }),

  verifyUserEmail: (userId) => api.post(`/admin/users/${userId}/verify-email`),

  resendUserVerification: (userId) => api.post(`/admin/users/${userId}/resend-verification`),

  // Reading Reports Management
  getReports: (params = {}) => api.get('/admin/reports', { params: cleanParams(params) }),

  getReportsStats: (params = {}) => api.get('/admin/reports/stats', { params: cleanParams(params) }),

  exportReports: (params = {}) => api.get('/admin/reports/export', {
    params: cleanParams(params),
    responseType: 'blob'
  }),

  resetAllReports: () => api.delete('/admin/reports/reset', {
    data: { confirmation: 'DELETE_ALL_REPORTS' }
  }),

  // Clean up all fake data (DANGER!)
  cleanupFakeData: () => api.post('/admin/cleanup-fake-data', {
    confirmation: 'DELETE_ALL_FAKE_DATA'
  }),

  // New comprehensive reset for all progress and points
  // axios DELETE takes its body as config.data
  resetAllProgress: (confirmationText) => api.delete('/reports/admin/reset-all-progress', {
    data: { confirmationText }
  }),

  // Family (group) management
  getFamilies: (params = {}) => api.get('/admin/families', { params: cleanParams(params) }),

  getFamily: (familyId) => api.get(`/admin/families/${familyId}`),

  createFamily: (familyData) => api.post('/admin/families', familyData),

  updateFamily: (familyId, changes) => api.patch(`/admin/families/${familyId}`, changes),

  deleteFamily: (familyId) => api.delete(`/admin/families/${familyId}`),

  regenerateFamilyInviteCode: (familyId) => api.post(`/admin/families/${familyId}/regenerate-invite-code`),

  // Adds the member and their active readers together, matching what joining
  // with an invite code does
  addFamilyMember: (familyId, payload) => api.post(`/admin/families/${familyId}/members`, payload),

  removeFamilyMember: (familyId, userId) => api.delete(`/admin/families/${familyId}/members/${userId}`),

  addFamilyReader: (familyId, readerId) => api.post(`/admin/families/${familyId}/readers`, { readerId }),

  removeFamilyReader: (familyId, readerId) => api.delete(`/admin/families/${familyId}/readers/${readerId}`),

  getFamilyIntegrity: (familyId) => api.get(`/admin/families/${familyId}/integrity`),

  repairFamily: (familyId) => api.post(`/admin/families/${familyId}/repair`),

  // Church management
  getChurches: (params = {}) => api.get('/admin/churches', { params: cleanParams(params) }),

  getChurch: (churchId) => api.get(`/admin/churches/${churchId}`),

  createChurch: (churchData) => api.post('/admin/churches', churchData),

  updateChurch: (churchId, changes) => api.patch(`/admin/churches/${churchId}`, changes),

  deleteChurch: (churchId) => api.delete(`/admin/churches/${churchId}`),

  regenerateChurchJoinCode: (churchId) => api.post(`/admin/churches/${churchId}/regenerate-join-code`),

  // Mirrors the church onto the user's readers, the same way joining does
  addChurchMember: (churchId, payload) => api.post(`/admin/churches/${churchId}/members`, payload),

  setChurchMemberStatus: (churchId, userId, status) =>
    api.post(`/admin/churches/${churchId}/members/${userId}/status`, { status }),

  removeChurchMember: (churchId, userId) => api.delete(`/admin/churches/${churchId}/members/${userId}`),

  getChurchIntegrity: (churchId) => api.get(`/admin/churches/${churchId}/integrity`),

  repairChurch: (churchId) => api.post(`/admin/churches/${churchId}/repair`),

  // Reader management
  getReaders: (params = {}) => api.get('/admin/readers', { params: cleanParams(params) }),

  getReader: (readerId) => api.get(`/admin/readers/${readerId}`),

  updateReader: (readerId, changes) => api.patch(`/admin/readers/${readerId}`, changes),

  deleteReader: (readerId) => api.delete(`/admin/readers/${readerId}`),

  getReaderImpact: (readerId) => api.get(`/admin/readers/${readerId}/impact`),

  reassignReader: (readerId, userId) => api.post(`/admin/readers/${readerId}/reassign`, { userId }),

  // Re-derives Bible completions and badges from the reader's reports
  resyncReader: (readerId) => api.post(`/admin/readers/${readerId}/resync`),

  // Per-report CRUD
  getReport: (reportId) => api.get(`/admin/reports/${reportId}`),

  createReport: (payload) => api.post('/admin/reports', payload),

  updateReport: (reportId, changes) => api.patch(`/admin/reports/${reportId}`, changes),

  deleteReport: (reportId) => api.delete(`/admin/reports/${reportId}`),

  // Chapters for one user, including ones an admin has already undone
  getReportsForUndo: (params = {}) => api.get('/admin/reports/for-undo', { params: cleanParams(params) }),

  undoReports: (reportIds, reason) => api.post('/admin/reports/undo', {
    reportIds,
    ...(reason ? { reason } : {})
  }),

  redoReports: (reportIds) => api.post('/admin/reports/redo', { reportIds }),

  undoChapters: (payload) => api.post('/admin/reports/undo-chapters', payload),

  // Scoped bulk delete: preview first, then pass the previewed count back
  previewBulkDeleteReports: (scope) => api.post('/admin/reports/bulk-delete/preview', scope),

  bulkDeleteReports: (scope) => api.post('/admin/reports/bulk-delete', scope),

  // Bulk "I read N chapters" logs, independent of per-chapter reports
  getRawChapterReports: (params = {}) => api.get('/admin/raw-chapter-reports', { params: cleanParams(params) }),

  createRawChapterReport: (payload) => api.post('/admin/raw-chapter-reports', payload),

  updateRawChapterReport: (id, changes) => api.patch(`/admin/raw-chapter-reports/${id}`, changes),

  deleteRawChapterReport: (id) => api.delete(`/admin/raw-chapter-reports/${id}`),

  // Public leaderboards with real names; opted-out readers carry competitorModeDisabled
  getLeaderboard: (params = {}) => api.get('/admin/leaderboard', { params: cleanParams(params) }),

  // Admin action trail (read-only by design)
  getAuditLog: (params = {}) => api.get('/admin/audit-log', { params: cleanParams(params) }),

  getAuditLogFacets: () => api.get('/admin/audit-log/facets'),

  // User and points management
  getUserDetails: (userId) => api.get(`/admin/users/${userId}/details`),
  getReaderPointsHistory: (readerId, params = {}) => api.get(`/admin/readers/${readerId}/points-history`, { params: cleanParams(params) }),
  getPointsSummary: () => api.get('/admin/points-summary'),

  getEvents: () => api.get('/admin/events'),

  startEvent: (event) => api.post('/admin/events', event),

  endEvent: (eventId) => api.post(`/admin/events/${eventId}/end`)
};

export default adminApi;
